import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Zap, TrendingUp, Activity } from "lucide-react";

const CorporateOverview = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const res = await fetch(`${API}/corporate/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setData(await res.json());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [token]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="animate-pulse h-28 bg-white rounded-2xl border border-emerald-100/30" />
        ))}
      </div>
    );
  }

  if (!data) return <p className="text-muted-foreground">Failed to load overview</p>;

  const stats = [
    { label: "Total Employees", value: data.total_employees, icon: Users, color: "text-emerald-600", bg: "bg-emerald-100" },
    { label: "Active Programs", value: data.active_programs, icon: Zap, color: "text-blue-600", bg: "bg-blue-100" },
    { label: "Active Enrollments", value: data.active_enrollments, icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-100" },
    { label: "Active Today", value: data.active_today, icon: Activity, color: "text-orange-600", bg: "bg-orange-100" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-secondary">Welcome, {data.organization_name}</h2>
        <p className="text-sm text-muted-foreground">Your corporate wellness dashboard — aggregated, anonymized data only.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label} className="rounded-2xl border-emerald-100/50" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}>
                <Icon className={`w-5 h-5 ${color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{value}</p>
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Privacy Notice */}
      <Card className="rounded-2xl bg-emerald-50/50 border-emerald-200/50 p-4">
        <p className="text-xs text-emerald-700">
          All data shown is aggregated and anonymized. Individual employee health data is never accessible from this dashboard, in compliance with GDPR, HIPAA, and global data protection standards.
        </p>
      </Card>
    </div>
  );
};

export default CorporateOverview;
